import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { warmupServer } from "@/api";
import { AppLayout } from "@/components/layout";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/features/auth/AuthProvider";
import { RequireAdmin } from "@/features/auth/RequireAdmin";
import { RequireAuth } from "@/features/auth/RequireAuth";
// SPEC-COOKIE-2 — consent context mounted inside AuthProvider (so
// recordConsent can read the current user) but outside Routes (so the
// banner shows on `/login` and `/cerez-politikasi` too).
import { ConsentProvider } from "@/features/consent/ConsentProvider";
import { CookieConsentBanner } from "@/features/consent/CookieConsentBanner";
import { CookieConsentModal } from "@/features/consent/CookieConsentModal";
import {
  AdminPage,
  CookiePolicyPage,
  BalanceSheetTab,
  ContractDetailPage,
  ContractEntryPage,
  ContractListPage,
  ContractsTab,
  DashboardPage,
  EngagementCreationPage,
  EngagementResultsPage,
  HistoryPage,
  JournalEntriesTab,
  LoginPage,
  MovementTab,
  NotFoundPage,
  PajesTab,
  PLImpactTab,
  ResetPasswordPage,
  ResultsPage,
  SettingsPage,
} from "@/pages";
import { ServiceProvider } from "@/services";
import { ThemeProvider } from "@/stores/theme-provider";

/**
 * SPEC-17 — bounce already-authed users away from /login so they don't
 * see the auth screen mid-session. Lives in App.tsx because the routes
 * are defined here and the wrapper is one-line.
 */
function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen w-full bg-black" aria-hidden />;
  }
  if (user) {
    return <Navigate to="/" replace />;
  }
  return <LoginPage />;
}

function App() {
  useEffect(() => {
    // Phase 6a.4: warm up backend on app load to mitigate cold-start delays
    warmupServer();
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <ConsentProvider>
        <ServiceProvider>
          <TooltipProvider>
            <BrowserRouter>
              <Routes>
              <Route path="/login" element={<LoginRoute />} />
              {/* SPEC-27 — password reset landing. Mounted OUTSIDE
                  RequireAuth so the recovery session (which is
                  short-lived) can finish updating the password before
                  the user is sent back to /login. */}
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* SPEC-COOKIE-2 — public cookie policy page (outside
                  RequireAuth so the banner link works for anonymous
                  visitors too). */}
              <Route
                path="/cerez-politikasi"
                element={<CookiePolicyPage />}
              />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <AppLayout />
                  </RequireAuth>
                }
              >
              <Route index element={<DashboardPage />} />
              {/* Legacy single-contract route — redirected to engagement
                  creation. Phase 6a.10 will wrap ContractFormContainer
                  inside the engagement flow. */}
              <Route
                path="new"
                element={<Navigate to="/engagements/new" replace />}
              />
              <Route path="results/:id" element={<ResultsPage />} />
              <Route path="history" element={<HistoryPage />} />
              <Route path="settings" element={<SettingsPage />} />

              {/* Phase 6a.8b — engagement routes (placeholder pages) */}
              <Route
                path="engagements/new"
                element={<EngagementCreationPage />}
              />
              {/* Phase 6a.19a — engagement-metadata edit. Reuses the
                  creation page in edit mode (mode detection via
                  useParams.id). The `/edit` suffix deconflicts with the
                  `:id/contracts/:idx` route below. */}
              <Route
                path="engagements/:id/edit"
                element={<EngagementCreationPage />}
              />
              <Route
                path="engagements/:id/contracts"
                element={<ContractListPage />}
              />
              <Route
                path="engagements/:id/contracts/:idx"
                element={<ContractEntryPage />}
              />
              {/* Phase 6a.12 — engagement results layout with tab routes.
                  Visiting `.../results` redirects to `.../results/balance-sheet`. */}
              <Route
                path="engagements/:id/results"
                element={<EngagementResultsPage />}
              >
                <Route
                  index
                  element={<Navigate to="balance-sheet" replace />}
                />
                <Route path="balance-sheet" element={<BalanceSheetTab />} />
                <Route path="movement" element={<MovementTab />} />
                <Route path="pl-impact" element={<PLImpactTab />} />
                <Route
                  path="journal-entries"
                  element={<JournalEntriesTab />}
                />
                <Route path="pajes" element={<PajesTab />} />
                <Route path="contracts" element={<ContractsTab />} />
              </Route>
              {/* Phase 6a.18 — per-contract detail page is a top-level route
                  (not nested under the `engagements/:id/results` layout),
                  so the engagement results tab strip does NOT render here.
                  Phase 6a.18b1 — `/results/` segment inserted into the path
                  to deconflict with the Phase 6a.8b edit route at
                  `engagements/:id/contracts/:idx` (both previously matched
                  `engagements/SOMETHING/contracts/SOMETHING_ELSE`, so the
                  earlier-declared edit route was winning and the detail
                  page never rendered). The contractId path param is
                  URL-encoded; ContractDetailPage decodes on read. */}
              <Route
                path="engagements/:engagementId/results/contracts/:contractId"
                element={<ContractDetailPage />}
              />

              {/* SPEC-23 — admin panel: user list + borrowed engagements. */}
              <Route
                path="admin"
                element={
                  <RequireAdmin>
                    <AdminPage />
                  </RequireAdmin>
                }
              />

              <Route path="*" element={<NotFoundPage />} />
            </Route>
              </Routes>
              {/* SPEC-COOKIE-2 — banner + manage modal overlay every
                  route inside the BrowserRouter (so the policy-link
                  Link works) and stay hidden once a current decision
                  has been recorded. */}
              <CookieConsentBanner />
              <CookieConsentModal />
            </BrowserRouter>
          </TooltipProvider>
        </ServiceProvider>
        </ConsentProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
